import { ChevronDown, Globe2, Menu, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import ksuLogo from "../assets/images/ksu-logo.png";
import { Link, useLocation } from "react-router-dom";

const navigationLinks = [
    {
        label: "الرئيسية",
        href: "/",
    },
    {
        label: "عن المؤتمر",
        href: "/about",
        hasDropdown: true,
        dropdownKey: "about",
        items: [
            { label: "نبذه عن الموْتمر", href: "/about" },
            { label: "الجدول الزمني", href: "/timeline" },
        ],
    },
    {
        label: "برنامج المؤتمر",
        href: "/program",
        hasDropdown: true,
        dropdownKey: "program",
        items: [
            { label: "الجلسات الحوارية", href: "/sessions" },
            { label: "ورش العمل", href: "/workshops" },
            { label: "المتحدثين", href: "/speakers" },
        ],
    },
    { label: "الجهات المشاركة", href: "/partners" },
    { label: "لجنة المؤتمر", href: "/committee" },
    { label: "التسجيل", href: "/registration" },
];

export default function Navbar() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const navRef = useRef(null);
    const location = useLocation();

    useEffect(() => {
        setOpenDropdown(null);
        setIsMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (navRef.current && !navRef.current.contains(event.target)) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const activeDropdown = navigationLinks.find((link) => link.dropdownKey === openDropdown);

    const toggleDropdown = (key) => {
        setOpenDropdown((current) => (current === key ? null : key));
    };

    return (
        <nav
            ref={navRef}
            dir="rtl"
            className="fixed inset-x-0 top-0 z-50 h-[115px] bg-white border-b border-slate-100 shadow-[0_6px_24px_rgba(15,23,42,0.04)]"
        >
            <div className="mx-auto flex h-full w-full max-w-[1440px] items-center justify-between px-5 sm:px-8 lg:px-[72px]">
                <Link to="/" className="" aria-label="جامعة الملك سعود">
                    <img src={ksuLogo} alt="logo" width={145} className="object-contain" />
                </Link>

                <div className="hidden flex-1 items-center justify-center lg:flex">
                    <div className="flex items-center gap-8 xl:gap-10">
                        {navigationLinks.map((link) =>
                            link.hasDropdown ? (
                                <button
                                    key={link.label}
                                    type="button"
                                    className="group flex items-center gap-1.5 text-base font-medium"
                                    aria-expanded={openDropdown === link.dropdownKey}
                                    aria-haspopup="true"
                                    onClick={() => toggleDropdown(link.dropdownKey)}
                                >
                                    <span>{link.label}</span>
                                    <ChevronDown
                                        className={`h-4 w-4 stroke-[2.4] transition-transform duration-200 ${
                                            openDropdown === link.dropdownKey ? "rotate-180" : ""
                                        }`}
                                        aria-hidden="true"
                                    />
                                </button>
                            ) : (
                                <Link
                                    key={link.label}
                                    to={link.href}
                                    className="group flex items-center gap-1.5 text-base font-medium"
                                    onClick={() => setOpenDropdown(null)}
                                >
                                    <span>{link.label}</span>
                                </Link>
                            )
                        )}
                    </div>
                </div>

                <div className="hidden shrink-0 items-center gap-3 md:flex">
                    <Link
                        to="/contact"
                        className="flex h-12 items-center justify-center rounded-lg bg-[#3E83B9] px-7 text-base font-semibold text-white"
                    >
                        تواصل معنا
                    </Link>
                </div>

                <button
                    type="button"
                    className="flex h-12 w-12 items-center justify-center rounded-xl border border-slate-200 text-slate-900 transition-colors duration-200 hover:border-[#006EB6] hover:text-[#006EB6] lg:hidden"
                    aria-label={isMenuOpen ? "إغلاق القائمة" : "فتح القائمة"}
                    aria-expanded={isMenuOpen}
                    onClick={() => {
                        setIsMenuOpen((open) => !open);
                        setOpenDropdown(null);
                    }}
                >
                    {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </div>

            {activeDropdown?.items ? (
                <div className="absolute inset-x-0 top-[115px] hidden w-full border-b border-slate-100 bg-white shadow-[0_12px_32px_rgba(15,23,42,0.08)] lg:block">
                    <div className="flex w-full items-center justify-center gap-24 p-[60px]">
                        {activeDropdown.items.map((item) => (
                            <Link
                                key={item.href}
                                to={item.href}
                                className="text-base font-medium text-slate-900 transition-colors duration-200 hover:text-[#006EB6]"
                                onClick={() => setOpenDropdown(null)}
                            >
                                {item.label}
                            </Link>
                        ))}
                    </div>
                </div>
            ) : null}

            {isMenuOpen && (
                <div className="absolute inset-x-0 top-[115px] border-b border-slate-100 bg-white shadow-xl lg:hidden">
                    <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-2 px-5 py-5 sm:px-8">
                        {navigationLinks.map((link) =>
                            link.hasDropdown && link.items ? (
                                <div key={link.label} className="flex flex-col">
                                    <button
                                        type="button"
                                        className="flex items-center justify-between rounded-xl px-4 py-3 text-[16px] font-semibold text-slate-800 transition-colors duration-200 hover:bg-slate-50 hover:text-[#006EB6]"
                                        onClick={() => toggleDropdown(link.dropdownKey)}
                                    >
                                        <span>{link.label}</span>
                                        <ChevronDown
                                            className={`h-4 w-4 transition-transform duration-200 ${
                                                openDropdown === link.dropdownKey ? "rotate-180" : ""
                                            }`}
                                            aria-hidden="true"
                                        />
                                    </button>
                                    {openDropdown === link.dropdownKey && (
                                        <div className="mr-4 mb-2 flex flex-col gap-1 border-r border-slate-200 pr-4">
                                            {link.items.map((item) => (
                                                <Link
                                                    key={item.href}
                                                    to={item.href}
                                                    className="rounded-lg px-3 py-2 text-[15px] font-medium text-slate-700 hover:bg-slate-50 hover:text-[#006EB6]"
                                                    onClick={() => {
                                                        setIsMenuOpen(false);
                                                        setOpenDropdown(null);
                                                    }}
                                                >
                                                    {item.label}
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Link
                                    key={link.label}
                                    to={link.href}
                                    className="flex items-center justify-between rounded-xl px-4 py-3 text-[16px] font-semibold text-slate-800 transition-colors duration-200 hover:bg-slate-50 hover:text-[#006EB6]"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    <span>{link.label}</span>
                                    {link.hasDropdown && <ChevronDown className="h-4 w-4" aria-hidden="true" />}
                                </Link>
                            )
                        )}
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                            <Link
                                to="/contact"
                                className="flex h-12 items-center justify-center rounded-xl bg-[#006EB6] px-7 text-[16px] font-bold text-white"
                                onClick={() => setIsMenuOpen(false)}
                            >
                                تواصل معنا
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
}
